import { useState } from 'react'
import { Menu } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
// import DarkModeToggle from '@/components/DarkModeToggle' // Temporarily hidden while light mode is forced.
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getStudioHref } from '@/lib/hostRouting'
import { AuthHide, PublicPrimaryCta } from '@/components/public/PublicCtas'
import { prefetchBookPage } from '@/lib/prefetchBook'
import { BRAND_LOGO } from '@/lib/brandAssets'

const NAV_LINKS = [
  { to: '/contact', label: 'Contact' },
  { to: '/privacy', label: 'Privacy & Terms' },
]

export default function SiteHeader() {
  const { hash, pathname } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  // Host-aware: on the public host this resolves to studio.retira.app/login.
  const loginHref = getStudioHref('/login')

  return (
    <>
    <header className="fixed inset-x-0 top-0 z-50 w-full max-w-full overflow-x-clip border-b border-border bg-card">
      <div className="relative mx-auto flex h-12 w-full min-w-0 max-w-6xl items-center justify-between gap-2 px-3 sm:h-14 sm:gap-3 sm:px-5">
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0 })}
          className="inline-flex shrink-0 items-center gap-2 text-lg font-semibold tracking-tight transition-colors hover:text-primary/80 sm:text-xl"
        >
          <picture>
            <source
              type="image/avif"
              srcSet={BRAND_LOGO.avifSrcSet}
              sizes="(min-width: 640px) 32px, 28px"
            />
            <img
              src={BRAND_LOGO.png}
              srcSet={BRAND_LOGO.pngSrcSet}
              sizes="(min-width: 640px) 32px, 28px"
              width="32"
              height="32"
              alt=""
              className="h-7 w-7 shrink-0 rounded object-cover sm:h-8 sm:w-8"
            />
          </picture>
          {/* Wordmark shows on wide screens, hides through the cramped mid-band
              where the inline nav appears, then reappears below sm – there the
              nav collapses into the mobile menu so there's room again. */}
          <span className="hidden min-[390px]:inline sm:hidden min-[700px]:inline">retira.app</span>
        </Link>

        <div className="absolute right-3 top-1/2 flex shrink-0 -translate-y-1/2 items-center gap-2 sm:static sm:translate-y-0 sm:gap-5">
          {/* Desktop: inline links */}
          <nav className="hidden min-w-0 items-center gap-5 text-sm sm:flex" aria-label="Main navigation">
            {NAV_LINKS.map((link) => {
              const className = [
                'whitespace-nowrap transition-colors hover:text-foreground',
                `${pathname}${hash}` === link.to ? 'text-foreground' : 'text-muted-foreground',
              ].join(' ')
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  aria-current={`${pathname}${hash}` === link.to ? 'page' : undefined}
                  className={className}
                >
                  {link.label}
                </Link>
              )
            })}
            <AuthHide>
              <a
                href={loginHref}
                className="whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground"
              >
                Log in
              </a>
            </AuthHide>
          </nav>

          <span className="hidden h-5 w-px bg-border sm:block" aria-hidden="true" />

          {/* <DarkModeToggle className="shrink-0" /> */}

          <Button asChild size="sm" variant="outline" className="hidden text-sm sm:inline-flex">
            <Link to="/book" onPointerEnter={prefetchBookPage} onFocus={prefetchBookPage}>Book a call</Link>
          </Button>

          <PublicPrimaryCta size="sm" compact className="shrink-0 whitespace-nowrap text-xs max-[359px]:px-2 sm:text-sm" />

          {/* Mobile: links tucked behind a menu */}
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild className="sm:hidden">
              <button
                type="button"
                aria-label="Open menu"
                className="inline-flex h-8 w-8 items-center justify-center border border-border bg-background text-muted-foreground transition-colors hover:text-foreground active:bg-muted"
              >
                <Menu className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={8} className="w-44 gap-1 p-1 sm:hidden">
              <Link
                to="/book"
                onClick={() => setMenuOpen(false)}
                onPointerEnter={prefetchBookPage}
                aria-current={`${pathname}${hash}` === '/book' ? 'page' : undefined}
                className={[
                  'flex h-9 w-full items-center px-2 text-sm transition-colors hover:bg-muted hover:text-foreground',
                  `${pathname}${hash}` === '/book' ? 'text-foreground' : 'text-muted-foreground',
                ].join(' ')}
              >
                Book a call
              </Link>
              {NAV_LINKS.map((link) => {
                const className = [
                  'flex h-9 w-full items-center px-2 text-sm transition-colors hover:bg-muted hover:text-foreground',
                  `${pathname}${hash}` === link.to ? 'text-foreground' : 'text-muted-foreground',
                ].join(' ')
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMenuOpen(false)}
                    aria-current={`${pathname}${hash}` === link.to ? 'page' : undefined}
                    className={className}
                  >
                    {link.label}
                  </Link>
                )
              })}
              <AuthHide>
                <a
                  href={loginHref}
                  onClick={() => setMenuOpen(false)}
                  className="flex h-9 w-full items-center px-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Log in
                </a>
              </AuthHide>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </header>
    {/* Reserve the fixed header's space in normal document flow.  A fixed
        public header cannot be displaced by nested scrolling or iOS rubber
        banding, while this spacer keeps every public route's first section
        exactly where it was before. */}
    <div className="h-12 shrink-0 sm:h-14" aria-hidden="true" />
    </>
  )
}
